import React, { Fragment, useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, CardBody, Button, Progress, Table, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getFromLocalStorage } from "../../../../hooks/useLocalStorage";
import { EAI_PARTNER, SALES_MANAGER } from "../../../../constant";
import { savePolicyDocumentDetails, bulkPolicyPunch } from "../../../../api/dealership";
import { bulkPolicyReset } from "../../../../redux/bulkPolicy/action";
import BulkDocumentStepperHeader from "./bulk-document-stepper-header";
import BulkPaymentConfirmModal from "./BulkPaymentConfirmModal";
import CustomerPaymentConfirmModal from "../policy-create/CustomerPaymentConfirmModal";
import "../policy-create/StepStyles.scss";
import convertToRupees from "../../../../utilities/convertToRupees";
import { useLocation } from "react-router-dom";
const BulkPaymentDetails = ({ onBack }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const location = useLocation();
  // Redux state
  const reduxValidationData = useSelector((state) => state.bulkPolicy.validationData || {});
  const reduxDiscountData = useSelector((state) => state.bulkPolicy.discountData || {});
  const reduxAddonData = useSelector((state) => state.bulkPolicy.addonData || {});
  const entireBulkPolicyState = useSelector((state) => state.bulkPolicy || {});
  console.log("Bulk Payment Details - Redux State:", reduxValidationData); // Debug log for entire bulk policy state
  // Local state
  const [loading, setLoading] = useState(false);
  const [paymentConfirmmodal, setPaymentConfirmModal] = useState(false);
  const [customerpaymentConfirmmodal, setCustomerPaymentConfirmModal] = useState(false);
  const [bulkErrorReportUrl, setBulkErrorReportUrl] = useState(null);
  // Bulk processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState([]);
  const location = useLocation();
  // User and role data
  const ROLE = JSON.parse(localStorage.getItem("ROLE"));
  const role_key = ROLE?.key;
  const role_id = ROLE?.role_id || '';
  const role_name = ROLE?.name || '';
  const user = role_key == "SALES_MANAGER" ? location.state.user : JSON.parse(localStorage.getItem("user"));
  const partnershipType = user?.partnerInfo?.type;

  // Get proposer details from Redux
  const proposerCommonDetails = reduxValidationData?.proposerCommonDetails || {};
  const discountedRecords = reduxDiscountData?.discountedRecords; // Get discounted records if discount was applied
  const displayRecords = discountedRecords || reduxValidationData?.successLog || [];
  const totalPremium = reduxValidationData?.totalPremium || 0;
  const availableBalance = reduxValidationData?.availableBalance || 0;
  //const availableBalance = reduxValidationData?.availableBalance || 0;
  const partnership = reduxValidationData?.partnership;
  const bulkFileUrl = reduxValidationData?.bulkFileUrl || '';
  const bulkQuotationNumber = reduxValidationData?.bulkQuotationNumber || '';

  // Calculate updated totals for discount-only and addon-only flows
  const updatedTotalPremium = useMemo(() => {
    if (discountedRecords && Array.isArray(discountedRecords) && discountedRecords.length > 0) {
      return discountedRecords.reduce((sum, record) => sum + (record.discount?.premiumAfterDiscount || 0), 0);
    }

    if (displayRecords && displayRecords.length > 0) {
      return displayRecords.reduce((sum, record) => {
        const recordId = record.record_index || record.id || record.proposer?.proposer_id;
        const basePremium = Number(record?.documentDetails?.base_premium || 0);
        const addonPremium = Number(reduxAddonData?.additionalCost?.[recordId] || record?.documentDetails?.addons_premium || 0);
        const subtotal = basePremium + addonPremium;
        return sum + subtotal + (subtotal * 0.18);
      }, 0);
    }

    return totalPremium;
  }, [discountedRecords, displayRecords, reduxAddonData, totalPremium]);

  const updatedAvailableBalance = availableBalance;

  // Define steps
  const bulkSteps = [
    { name: "Select Scheme & Type" },
    { name: "Review & Upload" },
    { name: "Proposer Details" },
    { name: "Payment" },
  ];

  const currentBulkStep = 3; // Payment step

  const togglePaymentConfirmModal = () => {
    setPaymentConfirmModal(!paymentConfirmmodal);
  };

  const toggleCustomerPaymentConfirmModal = () => {
    setCustomerPaymentConfirmModal(!customerpaymentConfirmmodal);
  };

  const buildPolicyPayload = (record) => {
    // Always use proposerCommonDetails from Redux for proposer details
    const documentDetails = record.documentDetails || {};
    const travellers = record.travellers || [];
    const nominees = record.nominees || {};
    const planDetails = record.planDetails || {};

    // Calculate proposer age from DOB if not present
    const calculateAge = (dob) => {
      if (!dob) return null;
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    // Helper to extract string value from Select object or return as-is
    const extractValue = (field) => {
      if (!field) return '';
      if (typeof field === 'object' && field.label) return field.label;
      return String(field);
    };

    // ── Addon data from Redux (keyed by record_index / record.id) ──────────────
    const recordId = record.record_index || record.id || record.proposer?.proposer_id;
    const recordAddons = reduxAddonData?.selectedServices?.[recordId] || record.addons || [];
    const recordAddonCost = reduxAddonData?.additionalCost?.[recordId] ?? (record.addonCost || 0);
    const individualCosts = reduxAddonData?.individualAddonCosts?.[recordId] || {};

    // Build structured addons array matching the required payload format
    const addonsPayload = recordAddons.map((svc) => {
      const serviceKey = `${svc.value}+${(svc.add_on_type || '').toLowerCase()}`;
      const addonAmount = individualCosts[serviceKey] ?? Number(svc.addon_amount || 0);
      return {
        service_id: Number(svc.value),
        add_on_type: (svc.add_on_type || '').toUpperCase(),
        addon_coa_value: Number(svc.addon_coa_value || 0),
        addon_coi_value: Number(svc.addon_coi_value || 0),
        addon_value_type: svc.addon_value_type || 'fixed',
        addon_amount: addonAmount,
      };
    });

    // Calculate addons_coi: sum of percentage-type insurance addons' COI cost
    const addonsCoi = recordAddons.reduce((sum, svc) => {
      if ((svc.add_on_type || '').toLowerCase() === 'insurance' &&
        (svc.addon_value_type || '').toLowerCase() !== 'fixed') {
        const coiPct = Number(svc.addon_coi_value || 0);
        const planCoi = planDetails?.plan_coi || documentDetails?.plan_coi || 0;
        return sum + (planCoi * coiPct) / 100;
      }
      return sum;
    }, 0);

    // ── Discount ──────────────────────────────────────────────────────────────
    const discountInfo = record.discount || null;
    const hasDiscount = discountInfo && discountInfo.percentage > 0;

    const finalBasePremium = hasDiscount ? discountInfo.finalBaseAmount : Number(documentDetails.base_premium || 0);
    const finalAddonsPremium = hasDiscount ? discountInfo.finalAddonsAmount : Number(recordAddonCost || documentDetails.addons_premium || 0);
    const finalTotalPremium = hasDiscount ? discountInfo.totalAmount : (finalBasePremium + finalAddonsPremium);
    const finalGstAmount = hasDiscount ? discountInfo.taxAmount : (finalTotalPremium * 0.18);
    const finalMemberPremium = hasDiscount ? discountInfo.premiumAfterDiscount : (finalTotalPremium + finalGstAmount);

    // ── Proposer ──────────────────────────────────────────────────────────────
    const proposerUniqueNumber = proposerCommonDetails?.proposer_unique_number || '';
    const hasProposerUniqueNumber = !!String(proposerUniqueNumber).trim();
    const isDomesticGeo = String(documentDetails?.geographical_cover_name || record?.geographical_cover_name || '').toLowerCase().trim() === 'domestic' || 
                          record?.geographical_cover_id === 11 || record?.geographical_cover_id === 17;
                          
    const mergedProposer = {
      proposer_passport_number: isDomesticGeo ? '' : (proposerCommonDetails?.proposer_passport_number || ''),
      proposer_unique_number: hasProposerUniqueNumber ? proposerUniqueNumber : '',
      proposer_dob: proposerCommonDetails?.proposer_dob || '',
      proposer_title: proposerCommonDetails?.proposer_title || 'Mr',
      proposer_first_name: proposerCommonDetails?.proposer_first_name || '',
      proposer_middle_name: proposerCommonDetails?.proposer_middle_name || '',
      proposer_last_name: proposerCommonDetails?.proposer_last_name || '',
      proposer_gender: proposerCommonDetails?.proposer_gender || 'MALE',
      proposer_oci: proposerCommonDetails?.proposer_oci || false,
      proposer_oci_file_path: proposerCommonDetails?.proposer_oci_file_path || null,
      number_of_travel_days: proposerCommonDetails?.number_of_travel_days || documentDetails.number_of_travel_days,
      proposer_age: proposerCommonDetails?.proposer_age || calculateAge(proposerCommonDetails?.proposer_dob),
      proposer_pin_code: proposerCommonDetails?.proposer_pin_code || '',
      proposer_address: proposerCommonDetails?.proposer_address || '',
      proposer_email: proposerCommonDetails?.proposer_email || '',
      proposer_mobile: proposerCommonDetails?.proposer_mobile || '',
      proposer_pan: proposerCommonDetails?.proposer_pan || '',
      state: extractValue(proposerCommonDetails?.state),
      city: extractValue(proposerCommonDetails?.city),
      proposer_country: proposerCommonDetails?.proposer_country || 'India',
      sponsor_name: proposerCommonDetails?.sponsor_name || '',
      sponsor_dob: proposerCommonDetails?.sponsor_dob || '',
      university_name: proposerCommonDetails?.university_name || '',
      university_address: proposerCommonDetails?.university_address || '',
      course_details: proposerCommonDetails?.course_details || '',
      sponsor_proposer_relation: proposerCommonDetails?.sponsor_proposer_relation || null,
    };

    // ── Document Details ──────────────────────────────────────────────────────
    const mergedDocumentDetails = {
      type_id: Number(record.type_id || documentDetails.type_id),
      geographical_cover_id: record.geographical_cover_id || documentDetails.geographical_cover_id,
      geographical_cover_name: documentDetails.geographical_cover_name || '',
      geo_country: documentDetails.geo_country || [],
      to_country: documentDetails.to_country || null,
      travel_start_date: documentDetails.travel_start_date,
      travel_end_date: documentDetails.travel_end_date,
      number_of_travel_days: documentDetails.number_of_travel_days,
      plan_id: documentDetails.plan_id || planDetails.plan_id,
      member_premium: finalMemberPremium,
      member_coverage: documentDetails.member_coverage || 0,
      quotation_number: bulkQuotationNumber || documentDetails.quotation_number || '',
      base_premium: finalBasePremium,
      addons_premium: finalAddonsPremium,
      company_id: documentDetails.company_id,
      punched_by: documentDetails.punched_by || 'bulk_upload',
      punched_at: documentDetails.punched_at || new Date().toISOString(),
      bulk_file_url: bulkFileUrl || '',
      partnership_id: role_key === SALES_MANAGER
        ? (partnership?.partnership_id || planDetails?.partnership_id || user?.partnerInfo?.partnership_id)
        : (planDetails?.partnership_id || null),
      created_by_role_id: role_id || null,
      created_by_role: role_name || null,
      // GST / Invoice fields
      customer_email: proposerCommonDetails?.customer_email || '',
      customer_mobile: proposerCommonDetails?.customer_mobile || '',
      customer_gstn: proposerCommonDetails?.customer_gstn || '',
      customer_company_name: proposerCommonDetails?.customer_company_name || '',
      customer_company_state: extractValue(proposerCommonDetails?.isGSTN ? (proposerCommonDetails?.customer_company_state || '') : ''),
      customer_company_city: extractValue(proposerCommonDetails?.isGSTN ? (proposerCommonDetails?.customer_company_city || '') : ''),
      customer_company_pin_code: proposerCommonDetails?.isGSTN ? Number(proposerCommonDetails?.customer_company_pin_code || 0) : null,
      isGSTN: proposerCommonDetails?.isGSTN || false,
      isCustomerInvoice: proposerCommonDetails?.isCustomerInvoice || false,
      customer_company_address: proposerCommonDetails?.customer_company_address || '',
      // Location
      from_state: documentDetails.from_state || '',
      from_city: documentDetails.from_city || '',
      to_state: documentDetails.to_state || '',
      to_city: documentDetails.to_city || '',
      // Pricing
      pricing_base_premium: hasDiscount ? discountInfo.basePremium : documentDetails.base_premium,
      pricing_addons_premium: finalAddonsPremium,
      pricing_msc_percent: hasDiscount ? discountInfo.finalMscPercentage : (documentDetails.pricing_msc_percent || 0),
      pricing_msc_amount: hasDiscount ? discountInfo.mscAmount : (documentDetails.pricing_msc_amount || 0),
      pricing_discount_percent: hasDiscount ? discountInfo.percentage : 0,
      pricing_discount_amount: hasDiscount ? discountInfo.amount : 0,
      pricing_new_base_premium: finalBasePremium,
      pricing_new_addons_premium: finalAddonsPremium,
      pricing_total_premium: finalTotalPremium,
      pricing_gst_percent: 18,
      pricing_gst_amount: finalGstAmount,
      pricing_member_premium: finalMemberPremium,
    };

    // ── Travellers ────────────────────────────────────────────────────────────
    const formattedTravellers = travellers.map((traveller) => ({
      traveller_passport: isDomesticGeo ? '' : (traveller.traveller_passport || traveller.traveller_passport_number || ''),
      relation_with_proposer: traveller.relation_with_proposer || 'Self',
      traveller_unique_number: traveller.traveller_unique_number || '',
      traveller_dob: traveller.traveller_dob || '',
      title: traveller.title || 'Mr',
      firstName: traveller.firstName || '',
      middleName: traveller.middleName || '',
      lastName: traveller.lastName || '',
      critical_illness: traveller.critical_illness == 'No Critical Illness' ? 'None' : (traveller.critical_illness || 'None'),
      pre_existing_disease: traveller.pre_existing_disease || 'No',
      suffering_since: traveller.suffering_since || '',
      ped_type: traveller.ped_type || '',
      under_medication: traveller.under_medication || '',
      trip_duration: Number(traveller.trip_duration) || Number(documentDetails.number_of_travel_days) || 0,
      plan_id: documentDetails.plan_id || planDetails.plan_id,
      base_coi: traveller.base_coi || documentDetails.plan_coi || 0,
      addons_coi: addonsCoi > 0 ? String(addonsCoi.toFixed(2)) : (traveller.addons_coi || documentDetails.addons_coi || '0'),
      same_as_proposer: traveller.same_as_proposer || false,
      member_premium: finalMemberPremium,
      member_coverage: traveller.member_coverage || documentDetails.member_coverage || 0,
      addons: addonsPayload.length > 0 ? addonsPayload : (traveller.addons || []),
      isOci: traveller.isOci || traveller.oci || false,
      oci_file_path: traveller.oci_file_path || traveller.oci_file || null,
      addons_premium: recordAddonCost > 0 ? recordAddonCost : finalAddonsPremium,
      gender: traveller.gender || 'MALE',
      age: traveller.age || calculateAge(traveller.traveller_dob) || 0,
    }));

    // ── Nominees ──────────────────────────────────────────────────────────────
    const formattedNominees = nominees.map((nominee) => ({
      nominee_dob: nominee.nominee_dob || '',
      nominee_title: nominee.title || nominee.nominee_title || '',
      nominee_firstName: nominee.firstName || nominee.nominee_firstName || '',
      nominee_middleName: nominee.middleName || nominee.nominee_middleName || '',
      nominee_lastName: nominee.lastName || nominee.nominee_lastName || '',
      nominee_relation: nominee.nominee_relation || 'Legal Heir',
      nominee_gender: nominee.gender || nominee.nominee_gender || '',
      nominee_same_as_proposer: nominee.nominee_same_as_proposer || false,
      toc_nominee: nominee.toc_nominee || false,
    }));

    // ── Final payload (top-level fields mirror the reference format) ──────────
    const finalPayload = {
      type_id: Number(record.type_id || documentDetails.type_id),
      geographical_cover_id: record.geographical_cover_id || documentDetails.geographical_cover_id,
      geographical_cover_name: documentDetails.geographical_cover_name || '',
      quotation_number: documentDetails.quotation_number || '',

      // Addon fields (top-level, required)
      addonCost: recordAddonCost,
      addons: addonsPayload,
      addons_coi: addonsCoi > 0 ? String(addonsCoi.toFixed(2)) : '0.00',

      proposer: mergedProposer,
      documentDetails: mergedDocumentDetails,
      travellers: formattedTravellers,
      nominees: formattedNominees,
    };

    // console.log("📦 FINAL PAYLOAD:", JSON.stringify(finalPayload, null, 2));
    return finalPayload;
  };

  // Bulk processing - Send ALL policies at once to the backend
  const processBulkPoliciesAtOnce = async (cpgOptions = {}) => {
    setIsProcessing(true);
    setLoading(true);
    setProcessingResults([]);

    try {
      // Build array of all policy payloads
      const allPolicies = displayRecords.map((record) => {
        const payload = buildPolicyPayload(record);
        if (cpgOptions?.isPayviaCustomer) {
          payload.isPayviaCustomer = 'true';
          payload.txnid = cpgOptions.txnid || '';
          payload.bank_ref_num = cpgOptions.bank_ref_num || '';
          payload.mode = cpgOptions.mode || 'CPG';
          if (payload.documentDetails) {
            payload.documentDetails.isPayviaCustomer = 'true';
          }
        }
        return payload;
      });
      console.log("🚀 Built payloads for all policies:", JSON.stringify(allPolicies, null, 2));
      // Prepare bulk payload
      const bulkPayload = {
        policies: allPolicies,
        isPayviaCustomer: cpgOptions?.isPayviaCustomer ? 'true' : undefined,
        txnid: cpgOptions?.txnid || '',
        bank_ref_num: cpgOptions?.bank_ref_num || '',
        mode: cpgOptions?.mode || '',
        cpgTxnDetails: cpgOptions?.isPayviaCustomer ? {
          txnid: cpgOptions?.txnid || '',
          bank_ref_num: cpgOptions?.bank_ref_num || '',
          mode: cpgOptions?.mode || 'CPG',
        } : undefined,
        partnership_id: role_key === SALES_MANAGER
          ? (user?.partnerInfo?.partnership_id)
          : user?.partnerInfo?.partnership_id || null,
        created_by_role: role_name || null,
        created_by_role_id: Number(role_id) || null,
        role_id: role_id || null,
      };
      console.log("🚀 Sending bulk payload to API:", JSON.stringify(bulkPayload, null, 2));
      // Call the bulk API
      const response = await bulkPolicyPunch(bulkPayload);

      if (response?.status === 200 && response?.data) {
        const responseData = response.data;
        const resultMessage = responseData.result || responseData.msg || 'Successfully Uploaded';

        // Check if response is a success message (string) or array of results
        if (typeof responseData.result === 'string' && responseData.code === 200) {
          // All policies successfully uploaded
          const displayResults = displayRecords.map((record, index) => ({
            index: index + 1,
            name: (() => {
              const t = record.travellers?.[0];
              const n = `${t?.title || ''} ${t?.firstName || ''} ${t?.lastName || ''}`.trim();
              return n || `Record ${index + 1}`;
            })(),
            quotation: record.documentDetails?.quotation_number || '',
            status: 'success',
            documentCode: 'Uploaded',
            message: resultMessage,
          }));

          setProcessingResults(displayResults);
          toast.success(`Bulk processing complete! All ${displayRecords.length} Documents uploaded successfully!`);

          setTimeout(() => {

            dispatch(bulkPolicyReset());
            // navigate('/documents/bulk');
            // toast.info("Starting new bulk upload. Redux state cleared.");

          }, 1000);
        } else if (Array.isArray(responseData.result)) {
          // Parse array results
          const results = responseData.result;
          const successfulDocs = results.filter(r => r.status === 'success');
          const failedDocs = results.filter(r => r.status === 'failure');

          const displayResults = results.map((result, index) => ({
            index: index + 1,
            name: (() => {
              const t = displayRecords[index]?.travellers?.[0];
              const n = `${t?.title || ''} ${t?.firstName || ''} ${t?.lastName || ''}`.trim();
              return n || `Record ${index + 1}`;
            })(),
            quotation: displayRecords[index]?.documentDetails?.quotation_number || '',
            status: result.status,
            documentCode: result.status === 'success' ? result.docId : null,
            message: result.error || 'Success',
          }));

          setProcessingResults(displayResults);
          toast.success(`Bulk processing complete! ${successfulDocs.length} succeeded, ${failedDocs.length} failed`);

          setTimeout(() => {

            dispatch(bulkPolicyReset());
            // navigate('/documents/bulk');
            // toast.info("Starting new bulk upload. Redux state cleared.");

          }, 1000);
        } else if (responseData.result && typeof responseData.result === 'object' && (responseData.result.erroFile || responseData.result.errorFile || responseData.result.file)) {
          // Backend returned a validation result object containing an error CSV URL
          const reportUrl = responseData.result.erroFile || responseData.result.errorFile || responseData.result.file || null;
          if (reportUrl) {
            // Preserve the report URL before clearing Redux so UI can show the download button
            setBulkErrorReportUrl(reportUrl);
            // Clear Redux + local state so user can re-punch
            dispatch(bulkPolicyReset());
            setProcessingResults([]);
            setIsProcessing(false);
            setLoading(false);
            toast.error(responseData.result.message || responseData.error || responseData.msg || 'Validation failed for some policies. Download the error sheet.');
            return;
          }
        } else {
          throw new Error('Unexpected response format');
        }

      }
      else {
        throw new Error('Bulk API returned non-200 status');
      }

    } catch (error) {
      console.error("❌ Bulk processing error:", error);
      toast.error(error?.response?.data?.error || error?.message || "Bulk processing failed");
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  const handleOfflinePaymentSubmit = async (eventOrDetails, cpgOptions = {}) => {
    if (eventOrDetails && typeof eventOrDetails.preventDefault === 'function') {
      eventOrDetails.preventDefault();
    }

    if (isProcessing) {
      toast.warning("Processing already in progress");
      return;
    }

    // Process all policies at once using bulk mode
    await processBulkPoliciesAtOnce(cpgOptions);
  };

  const handleCustomerPaymentSubmit = async (cpgDetails) => {
    if (cpgDetails && typeof cpgDetails.preventDefault === 'function') {
      cpgDetails.preventDefault();
    }
    
    setCustomerPaymentConfirmModal(false);
    
    // cpgDetails contains the SSE notifications (including documentId from backend bulk generation)
    let generatedDocs = [];
    if (cpgDetails?.documentId) {
      if (Array.isArray(cpgDetails.documentId)) {
        generatedDocs = cpgDetails.documentId;
      } else if (typeof cpgDetails.documentId === 'string') {
        generatedDocs = cpgDetails.documentId.split(',').map(id => id.trim());
      }
    }

    const displayResults = displayRecords.map((record, index) => ({
      index: index + 1,
      name: (() => {
        const t = record?.travellers?.[0];
        const n = `${t?.title || ''} ${t?.firstName || ''} ${t?.lastName || ''}`.trim();
        return n || `Record ${index + 1}`;
      })(),
      quotation: record?.documentDetails?.quotation_number || '',
      status: 'success',
      documentCode: generatedDocs[index] || cpgDetails?.documentId || 'Processed',
      message: 'Payment Successful',
    }));

    setProcessingResults(displayResults);
    toast.success(`Bulk processing complete! All ${displayRecords.length} Documents uploaded successfully!`);

    // Clear Redux state so user can start a new bulk upload (Backend webhook handles document creation)
    setTimeout(() => {
      dispatch(bulkPolicyReset());
    }, 1000);
  };

  return (
    <Fragment>
      <Card style={{ marginTop: "20px" }}>
        <CardBody className="btn-group-showcase">
          {/* Stepper Header */}
          <BulkDocumentStepperHeader
            currentStep={currentBulkStep}
            steps={bulkSteps}
            user={user}
          />

          <hr style={{ marginTop: "20px", marginBottom: "20px" }}></hr>

          <div className="form-row">
            {/* <Col md="1">
              <div className="stepper-container">
                <div className="line"></div>
                <div className="airplane" style={{ top: `40px` }}></div>
              </div>
            </Col> */}
            <Col md="12">
              <div className="col-12">
                <Card style={{ fontFamily: "Poppins !important" }}>
                  <CardBody className="btn-group-showcase">
                    {/* Payment Summary */}
                    <Row style={{ marginBottom: "20px" }}>
                      <Col md="12">
                        <h4><b>Payment Summary</b></h4>
                      </Col>
                    </Row>
                    {processingResults.length == 0 && (
                      <Row style={{ marginBottom: "30px" }}>
                        <Col md="6">
                          <div className="border rounded p-3">
                            <h6 className="text-muted">Total Documents</h6>
                            <h3><b>{displayRecords.length}</b></h3>
                          </div>
                        </Col>
                        <Col md="6">
                          <div className="border rounded p-3">
                            <h6 className="text-muted">Total Amount</h6>
                            <h3><b>{convertToRupees(updatedTotalPremium || 0)}</b></h3>
                          </div>
                        </Col>
                      </Row>)}

                    {/* Results Table - Only shown after processing completes */}
                    {processingResults.length > 0 && (
                      <Row style={{ marginBottom: "30px" }}>
                        <Col md="12">
                          <Card className="border">
                            <CardBody>
                              <h5 className="mb-3">
                                <i className="icofont icofont-check-circled"></i> Processing Results
                              </h5>

                              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                <Table bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="25%">Traveller Name</th>
                                      {/* <th width="20%">Quotation</th> */}
                                      <th width="15%">Status</th>
                                      <th width="20%">Document Code</th>
                                      <th width="15%">Message</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {processingResults.map((result, idx) => (
                                      result && (
                                        <tr key={idx}>
                                          <td>{result.index}</td>
                                          <td>{result.name}</td>
                                          {/* <td>{result.quotation}</td> */}
                                          <td>
                                            {result.status === 'success' ? (
                                              <Badge color="success">
                                                <i className="icofont icofont-check"></i> Success
                                              </Badge>
                                            ) : (
                                              <Badge color="danger">
                                                <i className="icofont icofont-close"></i> Failed
                                              </Badge>
                                            )}
                                          </td>
                                          <td>
                                            {result.documentCode ? (
                                              <code>{result.documentCode}</code>
                                            ) : '-'}
                                          </td>
                                          <td>
                                            <small>{result.message}</small>
                                          </td>
                                        </tr>
                                      )
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Payment Buttons */}
                    {bulkErrorReportUrl && (
                      <Row style={{ marginBottom: "15px" }}>
                        <Col md="12">
                          <div className="d-flex justify-content-end">
                            <Button
                              color="danger"
                              className="mr-2"
                              onClick={() => window.open(bulkErrorReportUrl, "_blank")}
                            >
                              <i className="fa fa-download" /> Download error sheet
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    )}
                    <div className="d-flex justify-content-between btn-mt">
                      <Button
                        color="warning"
                        className="px-4 rounded-pill stepper-back-btn"
                        onClick={onBack}
                        disabled={isProcessing}
                      >
                        <span className="mr-2">{"<"}</span> Back
                      </Button>

                      <div>
                        {partnershipType !== EAI_PARTNER && processingResults?.length <= 0 && (
                          <Button
                            color="primary"
                            className="px-4 rounded-pill stepper-next-btn"
                            onClick={togglePaymentConfirmModal}
                            disabled={isProcessing}
                          >
                            <i className="icofont icofont-wallet"></i> Pay via Wallet
                          </Button>
                        )}

                        {/* {processingResults?.length <= 0 && (
                          <Button 
                            color="primary" 
                            className="px-4 ml-3 rounded-pill stepper-next-btn" 
                            onClick={toggleCustomerPaymentConfirmModal}
                            disabled={isProcessing}
                          >
                            <i className="icofont icofont-wallet"></i> Pay via Customer
                          </Button>
                        )} */}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </Col>
          </div>
        </CardBody>
      </Card>

      {/* Payment Modals */}
      {paymentConfirmmodal && (
        <BulkPaymentConfirmModal
          isOpen={paymentConfirmmodal}
          toggle={togglePaymentConfirmModal}
          loading={loading}
          handleOfflinePaymentSubmit={handleOfflinePaymentSubmit}
          paymentMode={"OFFLINE"}
          bulkPaymentData={{
            totalPolicies: displayRecords.length,
            totalPremium: updatedTotalPremium,
            availableBalance: availableBalance,
            proposerEmail: proposerCommonDetails?.proposer_email || '',
            proposerMobile: proposerCommonDetails?.proposer_mobile || '',
            planName: displayRecords[0]?.planDetails?.title || 'Multiple Plans',
          }}
        />
      )}

      {customerpaymentConfirmmodal && (
        <CustomerPaymentConfirmModal
          isOpen={customerpaymentConfirmmodal}
          toggle={toggleCustomerPaymentConfirmModal}
          loading={loading}
          handleOfflinePaymentSubmit={handleCustomerPaymentSubmit}
          isMultiTraveller={true}
          multiTravellerGrandTotal={updatedTotalPremium}
          quotationNumber={bulkQuotationNumber || displayRecords[0]?.documentDetails?.quotation_number || displayRecords[0]?.quotation_number || ''}
        />
      )}
    </Fragment>
  );
};

export default BulkPaymentDetails;
